<template>
    <transition appear name="fade-transition">
        <div :class="classList">
            <v-text-field
                ref="input"
                v-model="inputValue"
                v-mask="maskValue"
                v-test-id
                class="edit__datetime__input"
                :name="name || uuid"
                type="text"
                outlined
                dense
                :rules="validators"
                :label="label"
                :hint="hint"
                :description="description"
                :placeholder="placeholder"
                persistent-placeholder
                background-color="background"
                color="primaryText"
                :hide-details="hideDetails !== null ? hideDetails : !form && _.isEmpty(validators) ? true : false"
                :disabled="disabled"
                :loading="loading"
                :readonly="readonly"
                :autocomplete="'off'"
                :error="error"
                :error-messages="errorMessages"
                :min="min"
                :max="max"
                :clearable="clearable"
                @blur="$event => $emit('blur', $event, inputValue)"
                @focus="$event => $emit('focus', $event, inputValue)"
                @keyup="$event => $emit('keyup', $event, inputValue)"
                @keydown="$event => $emit('keydown', $event, inputValue)"
                @click="$event => $emit('click', $event, inputValue)"
            >
                <template #prepend>
                    <slot name="prepend" />
                </template>
                <template #prepend-inner>
                    <slot name="prepend-inner" />
                </template>
                <template #append>
                    <slot name="append" />
                    <app-tooltip v-if="info || findSlot('info')" :value="info">
                        <v-icon dense small color="info" style="margin-bottom: 0.1rem">{{
                            getIcon('info', 'fad')
                        }}</v-icon>
                        <template v-if="findSlot('info')" #display>
                            <slot name="info" />
                        </template>
                    </app-tooltip>
                </template>
                <template #append-outer>
                    <slot name="append-outer" />
                </template>
            </v-text-field>
        </div>
    </transition>
</template>

<script>
import Edit from './Edit';

export default {
    name: 'EditDatetime',
    extends: Edit,
    props: {
        min: {
            type: String,
            default: null
        },
        max: {
            type: String,
            default: null
        },
        presetTime: {
            type: String,
            default: null
        },
        outputFormat: {
            type: String,
            default: 'iso8601'
        }
    },
    computed: {
        inputValue: {
            get() {
                try {
                    return this.toDatetime(this.value, `DD/MM/YYYY ${this.timeFormat}`);
                } catch (error) {
                    return this.value;
                }
            },
            set(value) {
                if (value && typeof value == 'string') {
                    let [date, time] = value.split(' ');

                    const dateLengths = date.split('-').map(part => {
                        return /^\d+$/.test(part) ? part.length : 0;
                    });

                    if (![4, 2, 2].every((l, i) => l === dateLengths[`${i}`])) {
                        return;
                    }

                    if (this.presetTime) {
                        value = this.toIso8601(`${date}T${this.presetTime}`, true);
                    } else {
                        const timeLengths = time.split(':').map(part => {
                            return /^\d+$/.test(part) ? part.length : 0;
                        });

                        if (timeLengths.length === 2) {
                            timeLengths.push(2);
                            value += ':00';
                        }

                        if (![2, 2, 2].every((l, i) => l === timeLengths[`${i}`])) {
                            return;
                        }
                        value = this.toIso8601(value, true);
                    }

                    if (!this.outputFormat || this.outputFormat === 'iso8601') {
                        this.$emit('input', value);
                    } else {
                        this.$emit('input', this.toDatetime(value, this.outputFormat));
                    }
                } else {
                    this.$emit('input', null);
                }
            }
        },
        timeFormat() {
            if (this.presetTime) {
                return `[${this.presetTime}]`;
            }

            return 'HH:mm:ss';
        },
        maskValue() {
            if (this.mask) {
                return this._.isFunction(this.mask) ? this.mask() : this.mask;
            }

            return this.getMask(this.presetTime ? 'date' : 'datetime', {
                prefix: this.prefixValue,
                suffix: this.suffixValue
            });
        }
    }
};
</script>

<style lang="scss">
.edit__datetime {
    min-width: 13rem;
}
</style>
